// gestureTiming — the two OPTIONAL overrides on the Play Gesture node, and the
// face override's fade-out envelope. Pure; unit-tested in
// tests/unit/gestureTiming.test.mjs.
//
// A gesture's length has always come from its gestures-table row (`duration`),
// and its body fade-out from GestureChannel's fixed 250 ms default. Neither was
// reachable from the graph, so a clip longer than its row's duration was cut
// mid-motion by a 250 ms ramp — the abrupt finish on `lean in`. These let the
// node say "play it for this long, and take this long to let go", without
// changing what an untouched node does.
//
// BLANK MEANS INHERIT, NOT ZERO. Absent is not a value, the same rule
// `face_drives` follows in the OGS manifest: a node with empty fields must be
// byte-identical to the node that existed before these fields did. A typed 0 is
// a real instruction (no fade — cut it) and is honoured.

/** The gestures-table default when a row states no duration. Mirrors FaceClipPlayer. */
export const DEFAULT_GESTURE_DURATION_MS = 3000

/**
 * A number from a node field / wired pin, or null when the author left it blank.
 *
 * Node fields arrive as strings ('' when cleared), wired pins as numbers, and
 * either can be absent. Anything that is not a finite number >= 0 reads as
 * BLANK rather than 0 — a half-typed '-' or a wired null must inherit, not
 * silently become a hard cut.
 */
export function readOptionalMs(raw) {
  if (raw === null || raw === undefined || raw === '') return null
  const n = Number(raw)
  return Number.isFinite(n) && n >= 0 ? n : null
}

/**
 * Resolve a gesture's play window and fade-out from its row plus the node's
 * overrides.
 *
 * @param {{duration?: number}} entry  the resolved gestures-table row
 * @param {{durationMs?: number|null, fadeOutMs?: number|null}} [overrides]
 * @returns {{durationMs: number, fadeOutMs: number|null}}
 *   `fadeOutMs` is null when the author left it blank — the caller then leaves
 *   each half on ITS OWN existing default, which are NOT the same number:
 *   GestureChannel fades the body over 250 ms, while a face override has no
 *   envelope at all and expires hard. Collapsing that to one default here would
 *   give every existing gesture a face fade it never had.
 */
export function resolveGestureTiming(entry, overrides = {}) {
  const durationOverride = readOptionalMs(overrides.durationMs)
  const durationMs = durationOverride ?? entry?.duration ?? DEFAULT_GESTURE_DURATION_MS

  let fadeOutMs = readOptionalMs(overrides.fadeOutMs)
  // A fade longer than the window would start before the gesture does. Clamp
  // rather than refuse: the author's intent ("let go slowly") is still served by
  // the longest fade that fits, and a silently-dropped override is worse.
  if (fadeOutMs !== null && fadeOutMs > durationMs) fadeOutMs = durationMs

  return { durationMs, fadeOutMs }
}

/**
 * A manual face override's weight right now, with its fade-out applied.
 *
 * Face overrides are a flat weight until `expiresAt`, then gone — which reads as
 * a snap whenever the clip has not settled by then. An override carrying
 * `fadeOutMs` ramps linearly to 0 across its final stretch instead, the same
 * shape GestureChannel's body envelope uses.
 *
 * An override with no `fadeOutMs` returns its flat weight unchanged, so this is
 * a no-op on every existing caller.
 */
export function overrideWeightAt(o, now) {
  if (!o) return 0
  const fade = readOptionalMs(o.fadeOutMs)
  if (fade === null || fade <= 0) return o.weight
  const remaining = o.expiresAt - now
  if (remaining >= fade) return o.weight
  if (remaining <= 0) return 0
  return o.weight * (remaining / fade)
}
